import { useState, useEffect } from 'react'

interface Category {
  id: number
  name: string
  parent_id: number
  custom_url?: {
    url: string
  }
}

interface CategoryMegaMenuProps {
  storeId: string
  apiUrl: string
}

export function CategoryMegaMenu({ storeId, apiUrl }: CategoryMegaMenuProps) {
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadCategories = async () => {
      if (!storeId || !apiUrl) {
        setError('No store connected')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/${storeId}/categories?page=1&limit=100`)

        if (!response.ok) {
          throw new Error('Failed to fetch categories')
        }

        const data = await response.json()
        setCategories(data.categories || [])
        setError(null)
      } catch (err) {
        console.error('Failed to load categories:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch categories')
      } finally {
        setIsLoading(false)
      }
    }

    loadCategories()
  }, [storeId, apiUrl])

  // Organize categories
  const topLevelCategories = categories.filter((cat) => cat.parent_id === 0)
  const getCategoryChildren = (parentId: number) => {
    return categories.filter((cat) => cat.parent_id === parentId)
  }

  if (!storeId) {
    return (
      <div className="w-[800px] min-w-[800px] p-8">
        <p className="text-sm text-destructive text-center">No store connected</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="w-[800px] min-w-[800px] p-8">
        <p className="text-sm text-muted-foreground text-center">Loading categories...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-[800px] min-w-[800px] p-8">
        <p className="text-sm text-destructive text-center">{error}</p>
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div className="w-[800px] min-w-[800px] p-8">
        <p className="text-sm text-muted-foreground text-center">No categories found</p>
      </div>
    )
  }

  return (
    <div className="w-[800px] min-w-[800px] p-6">
      <div className="grid grid-cols-4 gap-6">
        {topLevelCategories.map((category) => {
          const children = getCategoryChildren(category.id)
          return (
            <div key={category.id}>
              <a
                href={category.custom_url?.url || '#'}
                className="block text-sm font-semibold leading-none mb-3 hover:text-primary transition-colors"
              >
                {category.name}
              </a>
              {children.length > 0 && (
                <ul className="space-y-2">
                  {children.map((child) => (
                    <li key={child.id}>
                      <a
                        href={child.custom_url?.url || '#'}
                        className="block text-sm leading-snug text-muted-foreground hover:text-primary transition-colors"
                      >
                        {child.name}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
